/**
 * PreviewKit — shared layout primitives for component catalog previews.
 *
 * Usage pattern:
 *   <Preview>
 *     <Section label="Variants">
 *       <Row label="All types">
 *         <MyComponent variant="a" />
 *         <MyComponent variant="b" />
 *       </Row>
 *     </Section>
 *     <Section label="States">
 *       <Row label="Loading / Disabled">
 *         <MyComponent loading />
 *         <MyComponent disabled />
 *       </Row>
 *     </Section>
 *   </Preview>
 */
import React from 'react'
import { cn } from 'components/UI/cn'

/** Root wrapper — vertical stack with consistent spacing. */
export function Preview({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <div className={cn('flex flex-col gap-5 p-1 w-full', className)}>
      {children}
    </div>
  )
}

/**
 * Named section — groups related rows under a small all-caps heading.
 * Use for top-level groupings like "Variants", "Sizes", "States".
 */
export function Section({
  label,
  children,
  className,
}: {
  label?: string
  children: React.ReactNode
  className?: string
}) {
  return (
    <div className={cn('flex flex-col gap-3', className)}>
      {label && (
        <p className="text-xxs font-bold text-fg-tertiary uppercase tracking-widest pb-0.5 border-b border-line">
          {label}
        </p>
      )}
      {children}
    </div>
  )
}

/**
 * Horizontal row — wraps children in a flex row with an optional label above.
 * Use for showing multiple variants / states side by side.
 */
export function Row({
  label,
  children,
  className,
  align = 'center',
}: {
  label?: string
  children: React.ReactNode
  className?: string
  align?: 'center' | 'start' | 'end'
}) {
  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <p className="text-xxs font-medium text-fg-secondary">{label}</p>
      )}
      <div
        className={cn(
          'flex flex-wrap gap-2',
          align === 'center' && 'items-center',
          align === 'start' && 'items-start',
          align === 'end' && 'items-end',
          className
        )}
      >
        {children}
      </div>
    </div>
  )
}

/**
 * Vertical column — stacks children vertically with an optional label above.
 * Use for showing things that need vertical arrangement (e.g. form fields, lists).
 */
export function Col({
  label,
  children,
  className,
}: {
  label?: string
  children: React.ReactNode
  className?: string
}) {
  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {label && (
        <p className="text-xxs font-medium text-fg-secondary">{label}</p>
      )}
      {children}
    </div>
  )
}

/**
 * Inline label chip — a small grey caption rendered next to a component.
 * Use inside a Row when you need to label individual items.
 */
export function Chip({ children }: { children: React.ReactNode }) {
  return (
    <code className="text-xxs bg-surface-alt text-fg-tertiary px-1.5 py-0.5 rounded self-center">
      {children}
    </code>
  )
}
